import { useState } from 'react';
import { Plus, Home, Building2, Briefcase, HardHat, Store, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

// Mobile-friendly input class
const mobileInputClass = "bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none mt-1 h-12 text-base";
const mobileInputWithPrefixClass = "bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base";
const mobileInputWithSuffixClass = "bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pr-10 h-12 text-base";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function AddPropertyModal({ onPropertyAdded }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('single');

  // Single Family State - now with cost per foot inputs
  const [singleFamily, setSingleFamily] = useState({
    address: '',
    city: '',
    state: 'GA',
    bedrooms: 3,
    bathrooms: 2,
    square_feet: 1500,
    purchase_cost_per_ft: 100,
    arv_cost_per_ft: 150,
    repair_costs: 25000
  });

  // Multi Family State
  const [multiFamily, setMultiFamily] = useState({
    address: '',
    city: '',
    state: 'GA',
    unit_type: 'duplex',
    total_units: 2,
    square_feet: 2400,
    purchase_price: 250000,
    gross_monthly_rent: 2400,
    improved_monthly_rent: 3000,
    operating_expense_pct: 35,
    arv: 350000
  });

  // Portfolio State - with percentage-based operating expenses
  const [portfolio, setPortfolio] = useState({
    name: '',
    purchase_price: 1000000,
    gross_annual_revenue: 120000,
    improved_annual_revenue: 150000,
    operating_expense_pct: 35,
    manual_cap_rate: 8,
    target_cap_rate: 8
  });

  // Development / New Construction State
  const [development, setDevelopment] = useState({
    project_name: '',
    land_cost: 50000,
    build_cost: 200000,
    square_feet: 2000,
    noi: 24000,
    cap_rate: 8,
    build_cost_per_ft: 100,
    arv_per_ft: 175
  });

  // Commercial Property State
  const [commercial, setCommercial] = useState({
    property_name: '',
    address: '',
    city: '',
    state: 'GA',
    square_feet: 10000,
    purchase_price: 1500000,
    down_payment_pct: 25,
    interest_rate: 7,
    loan_term: 25,
    gross_annual_rent: 180000,
    vacancy_pct: 5,
    operating_expense_pct: 30,
    cap_rate: 8
  });

  // NNN (Triple Net Lease) State
  const [nnn, setNnn] = useState({
    property_name: '',
    tenant_name: '',
    tenant_credit: 'investment_grade',
    square_feet: 5000,
    lease_rate_per_ft: 25,
    lease_term_years: 10,
    annual_increases_pct: 2,
    purchase_price: 1500000,
    cap_rate: 6,
    down_payment_pct: 25
  });

  // Single Family Calculations
  const sfPurchasePrice = singleFamily.square_feet * singleFamily.purchase_cost_per_ft;
  const sfARV = singleFamily.square_feet * singleFamily.arv_cost_per_ft;
  const sfGrossProfit = sfARV - sfPurchasePrice - singleFamily.repair_costs;
  const sfRealtorCommission = sfARV * 0.06; // 6% of sale price
  const sfClosingCosts = sfARV * 0.02; // 2% closing costs
  const sfNetProceeds = sfGrossProfit - sfRealtorCommission - sfClosingCosts;
  const sfROI = sfPurchasePrice > 0 ? ((sfNetProceeds / (sfPurchasePrice + singleFamily.repair_costs)) * 100).toFixed(1) : 0;

  // Multi Family Calculations
  const mfCostPerFt = multiFamily.square_feet > 0 ? (multiFamily.purchase_price / multiFamily.square_feet).toFixed(2) : 0;
  const mfArvPerFt = multiFamily.square_feet > 0 ? (multiFamily.arv / multiFamily.square_feet).toFixed(2) : 0;
  const mfGrossAnnual = multiFamily.gross_monthly_rent * 12;
  const mfImprovedAnnual = multiFamily.improved_monthly_rent * 12;
  const mfOperatingExpenses = mfGrossAnnual * (multiFamily.operating_expense_pct / 100);
  const mfNOI = mfGrossAnnual - mfOperatingExpenses;
  const mfImprovedNOI = mfImprovedAnnual - (mfImprovedAnnual * (multiFamily.operating_expense_pct / 100));

  // Portfolio Calculations - Operating expenses as percentage of gross revenue
  const pOperatingExpenses = portfolio.gross_annual_revenue * (portfolio.operating_expense_pct / 100);
  const pImprovedOperatingExpenses = portfolio.improved_annual_revenue * (portfolio.operating_expense_pct / 100);
  const pCurrentNOI = portfolio.gross_annual_revenue - pOperatingExpenses;
  const pImprovedNOI = portfolio.improved_annual_revenue - pImprovedOperatingExpenses;
  
  // Purchase Cap Rate - calculated from purchase price and NOI
  const pPurchaseCapRate = portfolio.purchase_price > 0 ? ((pCurrentNOI / portfolio.purchase_price) * 100).toFixed(2) : 0;
  
  // Manual Cap Rate Valuation - user inputs their desired cap rate to see value
  const pManualCapValue = portfolio.manual_cap_rate > 0 ? (pCurrentNOI / (portfolio.manual_cap_rate / 100)) : 0;
  const pImprovedManualCapValue = portfolio.manual_cap_rate > 0 ? (pImprovedNOI / (portfolio.manual_cap_rate / 100)) : 0;
  
  // Valuations based on target cap rate for comparison
  const pCurrentValue = portfolio.target_cap_rate > 0 ? (pCurrentNOI / (portfolio.target_cap_rate / 100)) : 0;
  const pImprovedValue = portfolio.target_cap_rate > 0 ? (pImprovedNOI / (portfolio.target_cap_rate / 100)) : 0;
  const pEquityGain = pImprovedManualCapValue - portfolio.purchase_price;

  // Development / New Construction Calculations
  const devTotalCost = development.land_cost + development.build_cost;
  const devValueFromCap = development.cap_rate > 0 ? (development.noi / (development.cap_rate / 100)) : 0;
  const devActualBuildCostPerFt = development.square_feet > 0 ? (devTotalCost / development.square_feet).toFixed(2) : 0;
  const devARV = development.square_feet * development.arv_per_ft;
  const devEquityFromCap = devValueFromCap - devTotalCost;
  const devEquityFromARV = devARV - devTotalCost;
  const devSpreadPerFt = (development.arv_per_ft - parseFloat(devActualBuildCostPerFt)).toFixed(2);

  // Commercial Calculations
  const commPricePerFt = commercial.square_feet > 0 ? (commercial.purchase_price / commercial.square_feet).toFixed(2) : 0;
  const commEffectiveGrossIncome = commercial.gross_annual_rent * (1 - commercial.vacancy_pct / 100);
  const commOperatingExpenses = commEffectiveGrossIncome * (commercial.operating_expense_pct / 100);
  const commNOI = commEffectiveGrossIncome - commOperatingExpenses;
  const commActualCapRate = commercial.purchase_price > 0 ? ((commNOI / commercial.purchase_price) * 100).toFixed(2) : 0;
  const commDownPayment = commercial.purchase_price * (commercial.down_payment_pct / 100);
  const commLoanAmount = commercial.purchase_price - commDownPayment;
  // Monthly mortgage payment calculation (P&I)
  const commMonthlyRate = commercial.interest_rate / 100 / 12;
  const commNumPayments = commercial.loan_term * 12;
  const commMonthlyPayment = commLoanAmount > 0 && commMonthlyRate > 0 
    ? (commLoanAmount * (commMonthlyRate * Math.pow(1 + commMonthlyRate, commNumPayments))) / (Math.pow(1 + commMonthlyRate, commNumPayments) - 1)
    : 0;
  const commAnnualDebtService = commMonthlyPayment * 12;
  const commCashFlow = commNOI - commAnnualDebtService;
  const commCashOnCash = commDownPayment > 0 ? ((commCashFlow / commDownPayment) * 100).toFixed(2) : 0;
  const commDSCR = commAnnualDebtService > 0 ? (commNOI / commAnnualDebtService).toFixed(2) : 0;
  const commValueAtCap = commercial.cap_rate > 0 ? (commNOI / (commercial.cap_rate / 100)) : 0;

  // NNN Calculations
  const nnnAnnualRent = nnn.square_feet * nnn.lease_rate_per_ft;
  const nnnMonthlyRent = nnnAnnualRent / 12;
  const nnnPricePerFt = nnn.square_feet > 0 ? (nnn.purchase_price / nnn.square_feet).toFixed(2) : 0;
  const nnnActualCapRate = nnn.purchase_price > 0 ? ((nnnAnnualRent / nnn.purchase_price) * 100).toFixed(2) : 0;
  const nnnValueAtCap = nnn.cap_rate > 0 ? (nnnAnnualRent / (nnn.cap_rate / 100)) : 0;
  const nnnDownPayment = nnn.purchase_price * (nnn.down_payment_pct / 100);
  // Year 5 rent with annual increases
  const nnnYear5Rent = nnnAnnualRent * Math.pow(1 + nnn.annual_increases_pct / 100, 5);
  const nnnYear10Rent = nnnAnnualRent * Math.pow(1 + nnn.annual_increases_pct / 100, 10);
  const nnnYear5Value = nnn.cap_rate > 0 ? (nnnYear5Rent / (nnn.cap_rate / 100)) : 0;
  const nnnTotalRentOverLease = nnn.annual_increases_pct > 0 
    ? nnnAnnualRent * ((Math.pow(1 + nnn.annual_increases_pct / 100, nnn.lease_term_years) - 1) / (nnn.annual_increases_pct / 100))
    : nnnAnnualRent * nnn.lease_term_years;

  const getUnitCount = (type) => {
    switch(type) {
      case 'duplex': return 2;
      case 'triplex': return 3;
      case 'quadplex': return 4;
      case '6plex': return 6;
      default: return 2;
    }
  };

  const handleSaveSingleFamily = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deals/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: singleFamily.address,
          city: singleFamily.city,
          state: singleFamily.state,
          property_type: 'Single Family',
          bedrooms: parseInt(singleFamily.bedrooms),
          bathrooms: parseFloat(singleFamily.bathrooms),
          square_feet: parseInt(singleFamily.square_feet),
          list_price: sfPurchasePrice,
          arv_estimate: sfARV,
          rehab_cost: singleFamily.repair_costs
        })
      });
      if (response.ok) {
        const newDeal = await response.json();
        setOpen(false);
        if (onPropertyAdded) onPropertyAdded(newDeal);
      }
    } catch (err) {
      console.error('Error saving property:', err);
    }
  };

  const handleSaveMultiFamily = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deals/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: multiFamily.address,
          city: multiFamily.city,
          state: multiFamily.state,
          property_type: multiFamily.unit_type.charAt(0).toUpperCase() + multiFamily.unit_type.slice(1),
          bedrooms: getUnitCount(multiFamily.unit_type) * 2,
          bathrooms: getUnitCount(multiFamily.unit_type) * 1,
          square_feet: parseInt(multiFamily.square_feet),
          list_price: parseFloat(multiFamily.purchase_price),
          arv_estimate: parseFloat(multiFamily.arv),
          estimated_rent: parseFloat(multiFamily.gross_monthly_rent)
        })
      });
      if (response.ok) {
        const newDeal = await response.json();
        setOpen(false);
        if (onPropertyAdded) onPropertyAdded(newDeal);
      }
    } catch (err) {
      console.error('Error saving property:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          data-testid="add-property-btn"
          className="bg-green-600 hover:bg-green-500 text-black font-mono uppercase tracking-wider rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Property
        </Button>
      </DialogTrigger>
      
      <DialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-mono uppercase tracking-wider text-green-500">
            Property Analysis
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-3 sm:grid-cols-6 bg-zinc-900 rounded-none p-2 gap-2 h-auto">
            <TabsTrigger value="single" className="font-mono text-xs uppercase py-3 data-[state=active]:bg-green-600 data-[state=active]:text-black rounded-none">
              <Home className="w-4 h-4 mr-1" />
              Single
            </TabsTrigger>
            <TabsTrigger value="multi" className="font-mono text-xs uppercase py-3 data-[state=active]:bg-green-600 data-[state=active]:text-black rounded-none">
              <Building2 className="w-4 h-4 mr-1" />
              Multi
            </TabsTrigger>
            <TabsTrigger value="commercial" className="font-mono text-xs uppercase py-3 data-[state=active]:bg-green-600 data-[state=active]:text-black rounded-none">
              <Store className="w-4 h-4 mr-1" />
              Comm
            </TabsTrigger>
            <TabsTrigger value="nnn" className="font-mono text-xs uppercase py-3 data-[state=active]:bg-green-600 data-[state=active]:text-black rounded-none">
              <FileText className="w-4 h-4 mr-1" />
              NNN
            </TabsTrigger>
            <TabsTrigger value="development" className="font-mono text-xs uppercase py-3 data-[state=active]:bg-green-600 data-[state=active]:text-black rounded-none">
              <HardHat className="w-4 h-4 mr-1" />
              Dev
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="font-mono text-xs uppercase py-3 data-[state=active]:bg-green-600 data-[state=active]:text-black rounded-none">
              <Briefcase className="w-4 h-4 mr-1" />
              Portfolio
            </TabsTrigger>
          </TabsList>

          {/* SINGLE FAMILY TAB */}
          <TabsContent value="single" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs font-mono uppercase text-zinc-400">Address</Label>
                <Input
                  placeholder="123 Main St"
                  value={singleFamily.address}
                  onChange={(e) => setSingleFamily({...singleFamily, address: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">City</Label>
                <Input
                  placeholder="Atlanta"
                  value={singleFamily.city}
                  onChange={(e) => setSingleFamily({...singleFamily, city: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">State</Label>
                <Select value={singleFamily.state} onValueChange={(v) => setSingleFamily({...singleFamily, state: v})}>
                  <SelectTrigger className={mobileInputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Beds</Label>
                <Input
                  type="number"
                  value={singleFamily.bedrooms}
                  onChange={(e) => setSingleFamily({...singleFamily, bedrooms: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Baths</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={singleFamily.bathrooms}
                  onChange={(e) => setSingleFamily({...singleFamily, bathrooms: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Sq Ft</Label>
                <Input
                  type="number"
                  value={singleFamily.square_feet}
                  onChange={(e) => setSingleFamily({...singleFamily, square_feet: parseInt(e.target.value) || 0})}
                  className={mobileInputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Purchase $/SqFt</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={singleFamily.purchase_cost_per_ft}
                    onChange={(e) => setSingleFamily({...singleFamily, purchase_cost_per_ft: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">ARV $/SqFt</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={singleFamily.arv_cost_per_ft}
                    onChange={(e) => setSingleFamily({...singleFamily, arv_cost_per_ft: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Repair / Rehab Costs</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={singleFamily.repair_costs}
                  onChange={(e) => setSingleFamily({...singleFamily, repair_costs: parseFloat(e.target.value) || 0})}
                  className={mobileInputWithPrefixClass}
                />
              </div>
            </div>

            {/* Single Family Results */}
            <div className="bg-zinc-900 border border-green-500/50 p-4 space-y-3">
              <h4 className="text-xs font-mono uppercase text-green-500 tracking-wider">Flip Analysis</h4>
              
              <div className="grid grid-cols-2 gap-3 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Purchase Price</p>
                  <p className="text-lg font-mono font-bold">${sfPurchasePrice.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">{singleFamily.square_feet} sqft × ${singleFamily.purchase_cost_per_ft}/ft</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">ARV (Sale Price)</p>
                  <p className="text-lg font-mono font-bold text-green-500">${sfARV.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">{singleFamily.square_feet} sqft × ${singleFamily.arv_cost_per_ft}/ft</p>
                </div>
              </div>

              <div className="space-y-2 pb-3 border-b border-zinc-800">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Gross Profit (ARV - Purchase - Repairs)</span>
                  <span className="font-mono">${sfGrossProfit.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Less: Realtor Commission (6%)</span>
                  <span className="font-mono text-red-400">-${sfRealtorCommission.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Less: Closing Costs (2%)</span>
                  <span className="font-mono text-red-400">-${sfClosingCosts.toLocaleString()}</span>
                </div>
              </div>

              <div className="bg-green-500/10 -mx-4 -mb-4 px-4 py-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-mono text-green-400 uppercase">Net Proceeds</p>
                    <p className="text-3xl font-mono font-bold text-green-400">${sfNetProceeds.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-zinc-500">ROI</p>
                    <p className="text-2xl font-mono font-bold text-green-400">{sfROI}%</p>
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={handleSaveSingleFamily} className="w-full bg-green-600 hover:bg-green-500 text-black font-mono uppercase rounded-none">
              Save Property
            </Button>
          </TabsContent>

          {/* MULTI FAMILY TAB */}
          <TabsContent value="multi" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs font-mono uppercase text-zinc-400">Address</Label>
                <Input
                  placeholder="456 Oak Ave"
                  value={multiFamily.address}
                  onChange={(e) => setMultiFamily({...multiFamily, address: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">City</Label>
                <Input
                  placeholder="Atlanta"
                  value={multiFamily.city}
                  onChange={(e) => setMultiFamily({...multiFamily, city: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">State</Label>
                <Select value={multiFamily.state} onValueChange={(v) => setMultiFamily({...multiFamily, state: v})}>
                  <SelectTrigger className={mobileInputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Unit Type</Label>
                <Select value={multiFamily.unit_type} onValueChange={(v) => setMultiFamily({...multiFamily, unit_type: v, total_units: getUnitCount(v)})}>
                  <SelectTrigger className={mobileInputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="duplex">Duplex (2)</SelectItem>
                    <SelectItem value="triplex">Triplex (3)</SelectItem>
                    <SelectItem value="quadplex">Quadplex (4)</SelectItem>
                    <SelectItem value="6plex">6-Plex (6)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Total Sq Ft</Label>
                <Input
                  type="number"
                  value={multiFamily.square_feet}
                  onChange={(e) => setMultiFamily({...multiFamily, square_feet: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Purchase Price</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={multiFamily.purchase_price}
                    onChange={(e) => setMultiFamily({...multiFamily, purchase_price: e.target.value})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Current Monthly Rent (All Units)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={multiFamily.gross_monthly_rent}
                    onChange={(e) => setMultiFamily({...multiFamily, gross_monthly_rent: e.target.value})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Improved Monthly Rent</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={multiFamily.improved_monthly_rent}
                    onChange={(e) => setMultiFamily({...multiFamily, improved_monthly_rent: e.target.value})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Operating Expenses %</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    value={multiFamily.operating_expense_pct}
                    onChange={(e) => setMultiFamily({...multiFamily, operating_expense_pct: e.target.value})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">ARV</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={multiFamily.arv}
                    onChange={(e) => setMultiFamily({...multiFamily, arv: e.target.value})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
            </div>

            {/* Multi Family Results */}
            <div className="bg-zinc-900 border border-green-500/50 p-4 space-y-3">
              <h4 className="text-xs font-mono uppercase text-green-500 tracking-wider">Analysis Results</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Purchase $/SqFt</p>
                  <p className="text-lg font-mono font-bold text-yellow-500">${mfCostPerFt}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">ARV $/SqFt</p>
                  <p className="text-lg font-mono font-bold text-green-500">${mfArvPerFt}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Gross Annual Revenue</p>
                  <p className="text-lg font-mono font-bold">${mfGrossAnnual.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Improved Annual Revenue</p>
                  <p className="text-lg font-mono font-bold text-green-400">${mfImprovedAnnual.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Current NOI (Annual)</p>
                  <p className="text-lg font-mono font-bold">${mfNOI.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Improved NOI (Annual)</p>
                  <p className="text-lg font-mono font-bold text-green-400">${mfImprovedNOI.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <Button onClick={handleSaveMultiFamily} className="w-full bg-green-600 hover:bg-green-500 text-black font-mono uppercase rounded-none">
              Save Property
            </Button>
          </TabsContent>

          {/* COMMERCIAL TAB */}
          <TabsContent value="commercial" className="space-y-4 mt-4">
            <div className="text-center pb-2 border-b border-zinc-800 mb-4">
              <h3 className="text-sm font-mono uppercase text-green-500 tracking-wider">Commercial Property Analysis</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs font-mono uppercase text-zinc-400">Property Name</Label>
                <Input
                  placeholder="Main Street Retail"
                  value={commercial.property_name}
                  onChange={(e) => setCommercial({...commercial, property_name: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-mono uppercase text-zinc-400">Address</Label>
                <Input
                  placeholder="123 Main St"
                  value={commercial.address}
                  onChange={(e) => setCommercial({...commercial, address: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">City</Label>
                <Input
                  placeholder="Atlanta"
                  value={commercial.city}
                  onChange={(e) => setCommercial({...commercial, city: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">State</Label>
                <Select value={commercial.state} onValueChange={(value) => setCommercial({...commercial, state: value})}>
                  <SelectTrigger className={mobileInputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state} className="text-zinc-100">{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Square Feet</Label>
                <Input
                  type="number"
                  value={commercial.square_feet}
                  onChange={(e) => setCommercial({...commercial, square_feet: parseInt(e.target.value) || 0})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Purchase Price</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={commercial.purchase_price}
                    onChange={(e) => setCommercial({...commercial, purchase_price: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Gross Annual Rent</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={commercial.gross_annual_rent}
                    onChange={(e) => setCommercial({...commercial, gross_annual_rent: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
                <p className="text-xs text-zinc-600 mt-1">Monthly: ${(commercial.gross_annual_rent / 12).toLocaleString()}</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Vacancy Rate</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    value={commercial.vacancy_pct}
                    onChange={(e) => setCommercial({...commercial, vacancy_pct: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Operating Expenses %</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    value={commercial.operating_expense_pct}
                    onChange={(e) => setCommercial({...commercial, operating_expense_pct: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Target Cap Rate</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    value={commercial.cap_rate}
                    onChange={(e) => setCommercial({...commercial, cap_rate: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Down Payment %</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    value={commercial.down_payment_pct}
                    onChange={(e) => setCommercial({...commercial, down_payment_pct: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Interest Rate</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    step="0.125"
                    value={commercial.interest_rate}
                    onChange={(e) => setCommercial({...commercial, interest_rate: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Loan Term (Yrs)</Label>
                <Input
                  type="number"
                  value={commercial.loan_term}
                  onChange={(e) => setCommercial({...commercial, loan_term: parseInt(e.target.value) || 0})}
                  className={mobileInputClass}
                />
              </div>
            </div>

            {/* Commercial Results */}
            <div className="bg-zinc-900 border border-green-500/50 p-4 space-y-4">
              <h4 className="text-xs font-mono uppercase text-green-500 tracking-wider">Commercial Analysis</h4>
              
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Price per SF</p>
                  <p className="text-xl font-mono font-bold">${commPricePerFt}/sf</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Effective Gross Income</p>
                  <p className="text-xl font-mono font-bold">${commEffectiveGrossIncome.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">After {commercial.vacancy_pct}% vacancy</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Operating Expenses</p>
                  <p className="text-xl font-mono font-bold text-red-400">-${commOperatingExpenses.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Net Operating Income (NOI)</p>
                  <p className="text-xl font-mono font-bold text-green-400">${commNOI.toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Actual Cap Rate</p>
                  <p className="text-xl font-mono font-bold text-yellow-500">{commActualCapRate}%</p>
                  <p className="text-xs text-zinc-600">NOI ÷ Purchase Price</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Value at {commercial.cap_rate}% Cap</p>
                  <p className="text-xl font-mono font-bold text-green-400">${commValueAtCap.toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Down Payment</p>
                  <p className="text-lg font-mono font-bold">${commDownPayment.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Loan Amount</p>
                  <p className="text-lg font-mono font-bold">${commLoanAmount.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Annual Debt Service</p>
                  <p className="text-lg font-mono font-bold text-red-400">-${commAnnualDebtService.toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">DSCR</p>
                  <p className={`text-xl font-mono font-bold ${parseFloat(commDSCR) >= 1.25 ? 'text-green-400' : parseFloat(commDSCR) >= 1.0 ? 'text-yellow-500' : 'text-red-400'}`}>
                    {commDSCR}x
                  </p>
                  <p className="text-xs text-zinc-600">{parseFloat(commDSCR) >= 1.25 ? 'Strong' : parseFloat(commDSCR) >= 1.0 ? 'Marginal' : 'Below 1.0'}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Annual Cash Flow</p>
                  <p className={`text-xl font-mono font-bold ${commCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${commCashFlow.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-green-500/10 -mx-4 -mb-4 px-4 py-4">
                <p className="text-xs font-mono text-green-400 uppercase mb-2">Cash-on-Cash Return</p>
                <p className={`text-3xl font-mono font-bold ${parseFloat(commCashOnCash) >= 8 ? 'text-green-400' : parseFloat(commCashOnCash) >= 0 ? 'text-yellow-500' : 'text-red-400'}`}>
                  {commCashOnCash}%
                </p>
                <p className="text-xs text-zinc-500 mt-1">Cash Flow ÷ Down Payment</p>
              </div>
            </div>
          </TabsContent>

          {/* NNN (TRIPLE NET) TAB */}
          <TabsContent value="nnn" className="space-y-4 mt-4">
            <div className="text-center pb-2 border-b border-zinc-800 mb-4">
              <h3 className="text-sm font-mono uppercase text-green-500 tracking-wider">NNN (Triple Net Lease) Analysis</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs font-mono uppercase text-zinc-400">Property Name</Label>
                <Input
                  placeholder="Walgreens - Main St"
                  value={nnn.property_name}
                  onChange={(e) => setNnn({...nnn, property_name: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-mono uppercase text-zinc-400">Tenant Name</Label>
                <Input
                  placeholder="Walgreens Co."
                  value={nnn.tenant_name}
                  onChange={(e) => setNnn({...nnn, tenant_name: e.target.value})}
                  className={mobileInputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Tenant Credit</Label>
                <Select value={nnn.tenant_credit} onValueChange={(value) => setNnn({...nnn, tenant_credit: value})}>
                  <SelectTrigger className={mobileInputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="investment_grade" className="text-zinc-100">Investment Grade (AAA-BBB)</SelectItem>
                    <SelectItem value="national" className="text-zinc-100">National Credit</SelectItem>
                    <SelectItem value="regional" className="text-zinc-100">Regional Credit</SelectItem>
                    <SelectItem value="local" className="text-zinc-100">Local/Private</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Lease Term (Years)</Label>
                <Input
                  type="number"
                  value={nnn.lease_term_years}
                  onChange={(e) => setNnn({...nnn, lease_term_years: parseInt(e.target.value) || 0})}
                  className={mobileInputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Square Feet</Label>
                <Input
                  type="number"
                  value={nnn.square_feet}
                  onChange={(e) => setNnn({...nnn, square_feet: parseInt(e.target.value) || 0})}
                  className={mobileInputClass}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Lease Rate ($/SF/Year)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    step="0.5"
                    value={nnn.lease_rate_per_ft}
                    onChange={(e) => setNnn({...nnn, lease_rate_per_ft: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Annual Rent Increases</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    step="0.5"
                    value={nnn.annual_increases_pct}
                    onChange={(e) => setNnn({...nnn, annual_increases_pct: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Target Cap Rate</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    step="0.25"
                    value={nnn.cap_rate}
                    onChange={(e) => setNnn({...nnn, cap_rate: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Purchase Price</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={nnn.purchase_price}
                    onChange={(e) => setNnn({...nnn, purchase_price: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Down Payment %</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    value={nnn.down_payment_pct}
                    onChange={(e) => setNnn({...nnn, down_payment_pct: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
            </div>

            {/* NNN Results */}
            <div className="bg-zinc-900 border border-green-500/50 p-4 space-y-4">
              <h4 className="text-xs font-mono uppercase text-green-500 tracking-wider">NNN Analysis</h4>
              
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Annual Rent (Year 1)</p>
                  <p className="text-xl font-mono font-bold text-green-400">${nnnAnnualRent.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">Monthly: ${nnnMonthlyRent.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Price per SF</p>
                  <p className="text-xl font-mono font-bold">${nnnPricePerFt}/sf</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Actual Cap Rate</p>
                  <p className="text-xl font-mono font-bold text-yellow-500">{nnnActualCapRate}%</p>
                  <p className="text-xs text-zinc-600">Rent ÷ Purchase Price</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Value at {nnn.cap_rate}% Cap</p>
                  <p className="text-xl font-mono font-bold text-green-400">${nnnValueAtCap.toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Year 5 Rent</p>
                  <p className="text-xl font-mono font-bold">${nnnYear5Rent.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">With {nnn.annual_increases_pct}% annual increases</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Year 10 Rent</p>
                  <p className="text-xl font-mono font-bold">${nnnYear10Rent.toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Down Payment Required</p>
                  <p className="text-xl font-mono font-bold">${nnnDownPayment.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">{nnn.down_payment_pct}% of ${nnn.purchase_price.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Year 5 Value at {nnn.cap_rate}% Cap</p>
                  <p className="text-xl font-mono font-bold text-green-400">${nnnYear5Value.toLocaleString()}</p>
                </div>
              </div>

              <div className="bg-green-500/10 -mx-4 -mb-4 px-4 py-4">
                <p className="text-xs font-mono text-green-400 uppercase mb-2">Total Rent Over {nnn.lease_term_years}-Year Lease</p>
                <p className="text-3xl font-mono font-bold text-green-400">${nnnTotalRentOverLease.toLocaleString()}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Tenant Credit: <span className={`font-bold ${nnn.tenant_credit === 'investment_grade' ? 'text-green-400' : nnn.tenant_credit === 'national' ? 'text-blue-400' : 'text-yellow-500'}`}>
                    {nnn.tenant_credit === 'investment_grade' ? 'Investment Grade' : nnn.tenant_credit === 'national' ? 'National' : nnn.tenant_credit === 'regional' ? 'Regional' : 'Local'}
                  </span>
                </p>
              </div>
            </div>
          </TabsContent>

          {/* DEVELOPMENT / NEW CONSTRUCTION TAB */}
          <TabsContent value="development" className="space-y-4 mt-4">
            <div className="text-center pb-2 border-b border-zinc-800 mb-4">
              <h3 className="text-sm font-mono uppercase text-green-500 tracking-wider">Development / New Construction</h3>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Project Name</Label>
              <Input
                placeholder="New Build - Main St"
                value={development.project_name}
                onChange={(e) => setDevelopment({...development, project_name: e.target.value})}
                className={mobileInputClass}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Land Cost</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={development.land_cost}
                    onChange={(e) => setDevelopment({...development, land_cost: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Build Cost</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={development.build_cost}
                    onChange={(e) => setDevelopment({...development, build_cost: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Square Feet</Label>
              <Input
                type="number"
                value={development.square_feet}
                onChange={(e) => setDevelopment({...development, square_feet: parseInt(e.target.value) || 0})}
                className={mobileInputClass}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Projected NOI (Annual)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={development.noi}
                    onChange={(e) => setDevelopment({...development, noi: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Cap Rate</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    step="0.25"
                    value={development.cap_rate}
                    onChange={(e) => setDevelopment({...development, cap_rate: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Build Cost $/Ft (Manual)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={development.build_cost_per_ft}
                    onChange={(e) => setDevelopment({...development, build_cost_per_ft: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">ARV $/Ft (Manual)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={development.arv_per_ft}
                    onChange={(e) => setDevelopment({...development, arv_per_ft: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
              </div>
            </div>

            {/* Development Results */}
            <div className="bg-zinc-900 border border-green-500/50 p-4 space-y-4">
              <h4 className="text-xs font-mono uppercase text-green-500 tracking-wider">Development Analysis</h4>
              
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Total Development Cost</p>
                  <p className="text-xl font-mono font-bold">${devTotalCost.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">Land + Build</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Actual Cost/Ft (incl. Land)</p>
                  <p className="text-xl font-mono font-bold text-yellow-500">${devActualBuildCostPerFt}/ft</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Value from NOI/Cap</p>
                  <p className="text-xl font-mono font-bold text-green-400">${devValueFromCap.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">${development.noi.toLocaleString()} ÷ {development.cap_rate}%</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">ARV (from $/Ft)</p>
                  <p className="text-xl font-mono font-bold text-green-400">${devARV.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">{development.square_feet} × ${development.arv_per_ft}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">ARV $/Ft vs Build $/Ft</p>
                  <p className="text-xl font-mono font-bold">${development.arv_per_ft} - ${devActualBuildCostPerFt}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Spread per Ft</p>
                  <p className={`text-xl font-mono font-bold ${parseFloat(devSpreadPerFt) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${devSpreadPerFt}/ft
                  </p>
                </div>
              </div>

              <div className="bg-green-500/10 -mx-4 -mb-4 px-4 py-4">
                <p className="text-xs font-mono text-green-400 uppercase mb-2">Net Equity Gain from Development</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-zinc-500">From NOI/Cap</p>
                    <p className={`text-2xl font-mono font-bold ${devEquityFromCap >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${devEquityFromCap.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">From ARV</p>
                    <p className={`text-2xl font-mono font-bold ${devEquityFromARV >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${devEquityFromARV.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* PORTFOLIO TAB */}
          <TabsContent value="portfolio" className="space-y-4 mt-4">
            <div className="text-center pb-2 border-b border-zinc-800 mb-4">
              <h3 className="text-sm font-mono uppercase text-green-500 tracking-wider">Underwriting / Portfolio Appraisal Tool</h3>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Portfolio Name</Label>
              <Input
                placeholder="Main Street Portfolio"
                value={portfolio.name}
                onChange={(e) => setPortfolio({...portfolio, name: e.target.value})}
                className={mobileInputClass}
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Portfolio Purchase Price</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={portfolio.purchase_price}
                  onChange={(e) => setPortfolio({...portfolio, purchase_price: parseFloat(e.target.value) || 0})}
                  className={mobileInputWithPrefixClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Gross Annual Revenue</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={portfolio.gross_annual_revenue}
                    onChange={(e) => setPortfolio({...portfolio, gross_annual_revenue: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
                <p className="text-xs text-zinc-600 mt-1">Monthly: ${(portfolio.gross_annual_revenue / 12).toLocaleString()}</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Improved Annual Revenue</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={portfolio.improved_annual_revenue}
                    onChange={(e) => setPortfolio({...portfolio, improved_annual_revenue: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithPrefixClass}
                  />
                </div>
                <p className="text-xs text-zinc-600 mt-1">Monthly: ${(portfolio.improved_annual_revenue / 12).toLocaleString()}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Operating Expenses %</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    value={portfolio.operating_expense_pct}
                    onChange={(e) => setPortfolio({...portfolio, operating_expense_pct: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
                <p className="text-xs text-zinc-600 mt-1">Taxes, ins., maint.</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Cap Rate (Manual)</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    step="0.25"
                    value={portfolio.manual_cap_rate}
                    onChange={(e) => setPortfolio({...portfolio, manual_cap_rate: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
                <p className="text-xs text-zinc-600 mt-1">Your valuation cap</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-zinc-400">Market Cap Rate</Label>
                <div className="relative mt-1">
                  <Input
                    type="number"
                    step="0.25"
                    value={portfolio.target_cap_rate}
                    onChange={(e) => setPortfolio({...portfolio, target_cap_rate: parseFloat(e.target.value) || 0})}
                    className={mobileInputWithSuffixClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                </div>
                <p className="text-xs text-zinc-600 mt-1">For comparison</p>
              </div>
            </div>

            {/* Portfolio Results */}
            <div className="bg-zinc-900 border border-green-500/50 p-4 space-y-4">
              <h4 className="text-xs font-mono uppercase text-green-500 tracking-wider">Portfolio Valuation</h4>
              
              {/* Purchase Cap Rate Analysis */}
              <div className="bg-zinc-800/50 p-3 rounded border border-zinc-700">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-mono text-zinc-500">Purchase Cap Rate</p>
                    <p className="text-xs text-zinc-600">(Current NOI ÷ Purchase Price)</p>
                  </div>
                  <p className={`text-2xl font-mono font-bold ${parseFloat(pPurchaseCapRate) >= portfolio.target_cap_rate ? 'text-green-400' : 'text-yellow-500'}`}>
                    {pPurchaseCapRate}%
                  </p>
                </div>
                <p className="text-xs mt-2 text-zinc-500">
                  {parseFloat(pPurchaseCapRate) >= portfolio.target_cap_rate 
                    ? '✓ Purchase price is justified by NOI' 
                    : '⚠ Purchase price may be high relative to NOI'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Operating Expenses</p>
                  <p className="text-lg font-mono font-bold text-red-400">${pOperatingExpenses.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">{portfolio.operating_expense_pct}% of ${portfolio.gross_annual_revenue.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Improved Op. Expenses</p>
                  <p className="text-lg font-mono font-bold text-red-400">${pImprovedOperatingExpenses.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">{portfolio.operating_expense_pct}% of ${portfolio.improved_annual_revenue.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Current NOI (Annual)</p>
                  <p className="text-xl font-mono font-bold">${pCurrentNOI.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">Monthly: ${(pCurrentNOI / 12).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Improved NOI (Annual)</p>
                  <p className="text-xl font-mono font-bold text-green-400">${pImprovedNOI.toLocaleString()}</p>
                  <p className="text-xs text-zinc-600">Monthly: ${(pImprovedNOI / 12).toLocaleString()}</p>
                </div>
              </div>

              {/* Manual Cap Rate Valuation */}
              <div className="bg-zinc-800/50 p-3 rounded border border-green-500/30">
                <h5 className="text-xs font-mono uppercase text-green-400 mb-3">Your Valuation (at {portfolio.manual_cap_rate}% Cap)</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-mono text-zinc-500">Current Value</p>
                    <p className="text-xl font-mono font-bold text-yellow-500">${pManualCapValue.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-mono text-zinc-500">As Improved Value</p>
                    <p className="text-xl font-mono font-bold text-green-500">${pImprovedManualCapValue.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Market Cap Rate Comparison */}
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-zinc-800">
                <div>
                  <p className="text-xs font-mono text-zinc-500">Market Value (at {portfolio.target_cap_rate}% Cap)</p>
                  <p className="text-lg font-mono font-bold text-zinc-400">${pCurrentValue.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-500">Improved Market Value</p>
                  <p className="text-lg font-mono font-bold text-zinc-400">${pImprovedValue.toLocaleString()}</p>
                </div>
              </div>

              <div className="bg-green-500/10 -mx-4 -mb-4 px-4 py-3">
                <p className="text-xs font-mono text-green-400 uppercase">Equity Gain at {portfolio.manual_cap_rate}% Cap</p>
                <p className="text-3xl font-mono font-bold text-green-400">${pEquityGain.toLocaleString()}</p>
                <p className="text-xs text-zinc-500 mt-1">Improved Value - Purchase Price</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
